import React from 'react';
import './StaffDisplay.css';
import MeowSelector from './MeowSelector';

function StaffDisplay({ notes, bpm, timeSignature, onDeleteNote, selectedMeow, onSelectMeow }) {
  // 将十六进制song转换为简谱数字
  const getJianpuNumber = (songHex, meowHex) => {
    const songNum = parseInt(songHex, 16);
    
    // 中音区升调：01-05 对应 #1, #2, #4, #5, #6
    if (songNum >= 0x01 && songNum <= 0x05) {
      const sharpMap = {
        0x01: '#1',
        0x02: '#2',
        0x03: '#4',
        0x04: '#5',
        0x05: '#6'
      };
      return { number: sharpMap[songNum] || '#1', octave: 0 };
    }
    // 中音区自然音：06-0C 对应 1, 2, 3, 4, 5, 6, 7
    if (songNum >= 0x06 && songNum <= 0x0C) {
      const naturalMap = {
        0x06: '1',
        0x07: '2',
        0x08: '3',
        0x09: '4',
        0x0A: '5',
        0x0B: '6',
        0x0C: '7'
      };
      return { number: naturalMap[songNum] || '1', octave: 0 };
    }
    // 高音区升调：0D-11 对应 高#1, 高#2, 高#4, 高#5, 高#6
    if (songNum >= 0x0D && songNum <= 0x11) {
      const highSharpMap = {
        0x0D: '#1',
        0x0E: '#2',
        0x0F: '#4',
        0x10: '#5',
        0x11: '#6'
      };
      return { number: highSharpMap[songNum] || '#1', octave: 1 };
    }
    // 高音区自然音：12-18 对应 高1, 高2, 高3, 高4, 高5, 高6, 高7
    if (songNum >= 0x12 && songNum <= 0x18) {
      const highNaturalMap = {
        0x12: '1',
        0x13: '2',
        0x14: '3',
        0x15: '4',
        0x16: '5',
        0x17: '6',
        0x18: '7'
      };
      return { number: highNaturalMap[songNum] || '1', octave: 1 };
    }
    
    // 钢琴映射 (song  0x23-0x46)
    // 低音区: 0x23-0x2E (35-46), 中音区: 0x2F-0x3A (47-58), 高音区: 0x3B-0x46 (59-70)
    if (songNum >= 0x23 && songNum <= 0x46) {
      // 钢琴：从35开始 (0x23 = 35 = 低音1 Do)
      const offset = 35;
      const noteInOctave = (songNum - offset) % 12;
      const octave = Math.floor((songNum - offset) / 12);
      
      const noteMap = {
        0: '1',   // C (Do)
        1: '#1',  // C# (Do#)
        2: '2',   // D (Re)
        3: '#2',  // D# (Re#)
        4: '3',   // E (Mi)
        5: '4',   // F (Fa)
        6: '#4',  // F# (Fa#)
        7: '5',   // G (So)
        8: '#5',  // G# (So#)
        9: '6',   // A (La)
        10: '#6', // A# (La#)
        11: '7'   // B (Si)
      };
      
      return { number: noteMap[noteInOctave] || '1', octave: octave };
    }

    // 架子鼓：根据song值范围推断meow值
    // song 0x19-0x22 (25-34) -> meow 0x19-0x22
    if (songNum >= 0x19 && songNum <= 0x22) {
      const drumMap = {
        0x19: '大鼓/BD',
        0x1A: '通鼓0/T0',
        0x1B: '通鼓1/T1',
        0x1C: '通鼓2/T2',
        0x1D: '通鼓3/T3',
        0x1E: '军鼓/SD',
        0x1F: '吊镲1/C1',
        0x20: '吊镲2/C2',
        0x21: '吊叮镲/Ri',
        0x22: '踩镲/HH',
      };
      return { number: drumMap[songNum] || '1', octave: 0 };
    }

    // 默认返回
    return { number: '1', octave: 0 };
  };

  // 判断是高音还是低音（需要同时考虑meow和song）
  const getNoteType = (songHex, meowHex) => {
    const songNum = parseInt(songHex, 16);
    
    // 喵喵琴的编号：根据song值判断
    // 01-0C 是中音区，0D-18 是高音区
    if (songNum >= 0x01 && songNum <= 0x18) {
      if (songNum >= 0x01 && songNum <= 0x0C) {
        return 'middle';  // 喵喵琴中音区
      } else if (songNum >= 0x0D && songNum <= 0x18) {
        return 'high';    // 喵喵琴高音区
      }
    }
    
    // 钢琴按song值划分（0x23-0x46，三个区域）
    // 低音区: 0x23-0x2E (35-46)
    if (songNum >= 0x23 && songNum <= 0x2E) {
      return 'low';        // 低音区
    }
    // 中音区: 0x2F-0x3A (47-58)
    if (songNum >= 0x2F && songNum <= 0x3A) {
      return 'middle';     // 中音区
    }
    // 高音区: 0x3B-0x46 (59-70)
    if (songNum >= 0x3B && songNum <= 0x46) {
      return 'high';       // 高音区
    }
    
    // 架子鼓没有音区概念，返回middle作为默认
    if (songNum >= 0x19 && songNum <= 0x22) {
      return 'middle';
    }
    
    // 默认返回中音
    return 'middle';
  };


  // 获取乐器类型
  const getInstrumentType = (songHex) => {
    const songNum = parseInt(songHex, 16);
    // 架子鼓: 0x19-0x22
    if (songNum>= 0x19 && songNum<= 0x22) return 'drums';
    // 喵喵琴: 0x01-0x18
    if (songNum >= 0x01 && songNum <= 0x18) return 'meow';
    // 钢琴: 0x23-0x46
    if (songNum >= 0x23 && songNum <= 0x46) return 'piano';
    return 'unknown';
  };

  // 根据拍号将音符分组为小节
  const groupNotesIntoMeasures = () => {
    const measures = [];
    
    // 解析拍号
    const [numerator, denominator] = timeSignature.split('/').map(Number);
    
    // 计算"分母音符"的毫秒数
    // 分母=4：四分音符 = 60000 / BPM 毫秒
    // 分母=8：八分音符 = (60000 / BPM) / 2 毫秒
    // 公式：分母音符时长 = (60000 / BPM) × (4 / 分母)
    const quarterBeatDuration = 60000 / bpm; // 四分音符的毫秒数
    const denominatorBeatDuration = quarterBeatDuration * (4 / denominator);
    
    let currentMeasure = [];
    let currentBeats = 0; // 累计多少个"分母音符"
    
    notes.forEach((note) => {
      const timeMs = Math.abs(note.time || 250);
      // 计算这个音符相当于多少个"分母音符"
      const noteBeats = timeMs / denominatorBeatDuration;
      
      // 如果加入这个音符会超过小节要求的音符数，开始新小节
      if (currentBeats > 0 && currentBeats + noteBeats > numerator) {
        measures.push(currentMeasure);
        currentMeasure = [];
        currentBeats = 0;
      }
      
      currentMeasure.push(note);
      currentBeats += noteBeats;
      
      // 如果当前小节已满（达到numerator个分母音符），开始新小节
      if (currentBeats >= numerator) {
        measures.push(currentMeasure);
        currentMeasure = [];
        currentBeats = 0;
      }
    });
    
    // 添加最后一个未满的小节
    if (currentMeasure.length > 0) {
      measures.push(currentMeasure);
    }
    
    return measures;
  };

  const measures = groupNotesIntoMeasures();

  // 解析拍号
  const [beatsPerMeasure, beatUnit] = timeSignature.split('/').map(Number);

  return (
    <div className="staff-display">
      <div className="staff-header">
        <h2>🎼 简谱展示区</h2>
        <div className="time-signature-display">
          <div className="time-sig-fraction">
            <div className="time-sig-top">{beatsPerMeasure}</div>
            <div className="time-sig-line"></div>
            <div className="time-sig-bottom">{beatUnit}</div>
          </div>
        </div>
      </div>
      
      {/* 喵喵选择器 */}
      <div className="meow-selector-container">
        <MeowSelector selectedMeow={selectedMeow} onSelectMeow={onSelectMeow} />
      </div>
      
      <div className="jianpu-container">
        {notes.length === 0 ? (
          <div className="empty-message">
            <p>🎵 暂无音符</p>
            <p className="hint">在右侧选择乐器和音符来添加</p>
          </div>
        ) : (
          <div className="jianpu-score">
            {/* 简谱主区域 */}
            <div className="jianpu-staff">
              {measures.map((measure, measureIndex) => {
                // 计算当前小节之前的音符总数
                let notesBefore = 0;
                for (let i = 0; i < measureIndex; i++) {
                  notesBefore += measures[i].length;
                }
                
                return (
                <div key={measureIndex} className="jianpu-measure">
                  {measure.map((note, noteIndex) => {
                    const globalIndex = notesBefore + noteIndex;
                    const { number } = getJianpuNumber(note.song, note.meow);
                    const noteType = getNoteType(note.song, note.meow);
                    const instrumentType = getInstrumentType(note.song);
                    
                    // 根据time(毫秒)计算节拍标记
                    const timeMs = note.time || 250;
                    const isRest = (note.meow === '00' && note.song === '00') || timeMs < 0;
                    const absTimeMs = Math.abs(timeMs);
                    // 正确计算拍数：时长(ms) / 一拍的时长(ms)
                    const beatDuration = 60000 / bpm;
                    const durationInBeats = absTimeMs / beatDuration;
                    let underlines = 0;      // 下方横线（短音符）
                    let rightLines = 0;      // 右侧横线（长音符）
                    
                    if (isRest) {
                      // 休止符不加横线
                      underlines = 0;
                      rightLines = 0;
                    } else if (durationInBeats < 1) {
                      // 短于1个四分音符：下方加横线
                      if (durationInBeats <= 0.125) underlines = 3;    // 1/32拍（0.125个四分音符）
                      else if (durationInBeats <= 0.25) underlines = 2; // 1/16拍（0.25个四分音符）
                      else underlines = 1;                              // 1/8拍（0.5个四分音符）
                    } else if (durationInBeats <= 1) {
                      // 1个四分音符：无横线
                      underlines = 0;
                      rightLines = 0;
                    } else {
                      // 长于1个四分音符：右侧加横线
                      if (durationInBeats <= 2) rightLines = 1;      // 2个四分音符：1个延长线
                      else if (durationInBeats <= 3) rightLines = 2; // 3个四分音符：2个延长线
                      else if (durationInBeats <= 4) rightLines = 3; // 4个四分音符：3个延长线
                      else rightLines = Math.min(Math.floor(durationInBeats - 1), 5); // 更长
                    }
                    
                    return (
                      <div key={noteIndex} className="jianpu-note-wrapper">
                        {/* 删除按钮 */}
                        <button
                          className="delete-note-btn-inline"
                          onClick={() => onDeleteNote(globalIndex)}
                          title="删除此音符"
                        >
                          ×
                        </button>
                        
                        {/* 音符和右侧横线 */}
                        <div 
                          className="note-horizontal-group"
                          style={{
                            width: `${durationInBeats * 66}px`
                          }}
                        >
                          {/* 音符主体（包含高低音点） */}
                          <div className="note-vertical-group">
                            {/* 高音点 */}
                            {!isRest && instrumentType !== 'drums' && noteType === 'high' && (
                              <div className="high-dot">•</div>
                            )}
                            
                            {/* 音符数字 */}
                            <div className={`jianpu-note-content ${noteType} ${isRest ? 'rest' : ''}`}>
                              {isRest ? (
                                <span className="note-digit rest-symbol">0</span>
                              ) : instrumentType === 'drums' ? (
                                <span className="drum-notation">
                                  {number}
                                </span>
                              ) : (
                                <span className="note-digit">{number}</span>
                              )}
                            </div>
                            
                            {/* 低音点（在数字正下方） */}
                            {!isRest && instrumentType !== 'drums' && noteType === 'low' && (
                              <div className="low-dot">•</div>
                            )}
                            
                            {/* 下方横线（短音符，在低音点下方） */}
                            {underlines > 0 && (
                              <div className="duration-underlines">
                                {[...Array(underlines)].map((_, i) => (
                                  <div key={i} className="underline-display"></div>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          {/* 右侧延长线（长音符）- 横向排列 */}
                          {rightLines > 0 && (
                            <div className="duration-right-lines">
                              {[...Array(rightLines)].map((_, i) => (
                                <span key={i} className="right-line-display"></span>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* 音符序号 */}
                        <div className="note-sequence">{globalIndex + 1}</div>
                      </div>
                    );
                  })}
                  
                  {/* 小节线 */}
                  {measureIndex < measures.length - 1 && (
                    <div className="measure-bar">|</div>
                  )}
                </div>
              );
              })}
              
              {/* 结束线 */}
              {notes.length > 0 && <div className="measure-bar-end">||</div>}
            </div>
          </div>
        )}
      </div>

      {/* 音符列表 */}
      <div className="notes-list">
        <h3>音符列表 ({notes.length})</h3>
        <div className="notes-list-container">
          {notes.map((note, index) => {
            const { number } = getJianpuNumber(note.song, note.meow);
            const noteType = getNoteType(note.song, note.meow);
            const instrumentType = getInstrumentType(note.song);
            let displayNote = number;
            if (instrumentType !== 'drums') {
              if (noteType === 'high') displayNote = `${number}(高)`;
              if (noteType === 'low') displayNote = `${number}(低)`;
              if (noteType === 'middle') displayNote = `${number}(中)`;
            } else {
              displayNote = number; // 架子鼓直接使用number（已经是"大鼓/BD"格式）
            }
            
            // 节拍显示（从time毫秒转换）
            const timeMs = note.time || 250;
            const isRestNote = (note.meow === '00' && note.song === '00') || timeMs < 0;
            const absTimeMs = Math.abs(timeMs);
            // 正确计算拍数：时长(ms) / 一拍的时长(ms)
            const beatDurationForDisplay = 60000 / bpm;
            const durationInBeats = absTimeMs / beatDurationForDisplay;
            let durationLabel = '';
            
            if (isRestNote) {
              durationLabel = '休止符';
            } else if (durationInBeats <= 0.125) {
              durationLabel = '1/32拍';
            } else if (durationInBeats <= 0.25) {
              durationLabel = '1/16拍';
            } else if (durationInBeats <= 0.5) {
              durationLabel = '1/8拍(半拍)';
            } else if (durationInBeats <= 1) {
              durationLabel = '1/4拍(1拍)';
            } else if (durationInBeats <= 2) {
              durationLabel = '1/2拍(2拍)';
            } else if (durationInBeats <= 3) {
              durationLabel = '3/4拍(3拍)';
            } else if (durationInBeats <= 4) {
              durationLabel = '1全拍(4拍)';
            } else {
              durationLabel = durationInBeats + '个四分音符';
            }
            
            return (
              <div key={index} className="note-list-item">
                <span className="note-index">{index + 1}</span>
                <span className="note-detail">
                  {isRestNote ? '休止符 (0)' : displayNote}
                </span>
                <span className="note-duration">{durationLabel} ({absTimeMs}ms)</span>
                <span className="note-song">0x{note.song}</span>
                <span className="note-meow">猫{note.meow}</span>
                <button
                  className="note-delete-btn"
                  onClick={() => onDeleteNote(index)}
                >
                  删除
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default StaffDisplay;

